"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ParallelAdapter = exports.HeikoAdapter = exports.parallelTokensConfig = exports.heikoRoutersConfig = exports.parallelRoutersConfig = void 0;
const storage_1 = require("@acala-network/sdk/utils/storage");
const sdk_core_1 = require("@acala-network/sdk-core");
const rxjs_1 = require("rxjs");
const balance_adapter_1 = require("../balance-adapter");
const base_chain_adapter_1 = require("../base-chain-adapter");
const configs_1 = require("../configs");
const errors_1 = require("../errors");
const xtokens_helper_1 = require("../utils/xtokens-helper");
const DEST_WEIGHT = "Unlimited";
exports.parallelRoutersConfig = [
    {
        to: "interlay",
        token: "IBTC",
        xcm: {
            // during chopsticks test: fee = 71 Add 10x margin
            fee: { token: "IBTC", amount: "710" },
            weightLimit: DEST_WEIGHT,
        },
    },
    {
        to: "interlay",
        token: "INTR",
        xcm: {
            // during chopsticks test: fee = 21_660_472 Add 10x margin
            fee: { token: "INTR", amount: "216604720" },
            weightLimit: DEST_WEIGHT,
        },
    },
];
exports.heikoRoutersConfig = [
    {
        to: "kintsugi",
        token: "KBTC",
        xcm: {
            // chopsticks tests indicate fees are 107
            fee: { token: "KBTC", amount: "1070" },
            weightLimit: DEST_WEIGHT,
        },
    },
    {
        to: "kintsugi",
        token: "KINT",
        xcm: {
            fee: { token: "KINT", amount: "2471893330" },
            weightLimit: DEST_WEIGHT,
        },
    },
];
exports.parallelTokensConfig = {
    parallel: {
        // ed confirmed via assets.asset(<id>)
        IBTC: { name: "IBTC", symbol: "IBTC", decimals: 8, ed: "1" },
        INTR: { name: "INTR", symbol: "INTR", decimals: 10, ed: "1" },
    },
    heiko: {
        // ed confirmed via assets.asset(<id>)
        KBTC: { name: "KBTC", symbol: "KBTC", decimals: 8, ed: "0" },
        KINT: { name: "Kintsugi", symbol: "KINT", decimals: 12, ed: "0" },
    },
};
const SUPPORTED_TOKENS = {
    IBTC: 122,
    INTR: 120,
    KBTC: 121,
    KINT: 119, // asset id 119
};
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const createBalanceStorages = (api) => {
    return {
        balances: (address) => storage_1.Storage.create({
            api,
            path: "derive.balances.all",
            params: [address],
        }),
        assets: (tokenId, address) => storage_1.Storage.create({
            api,
            path: "query.assets.account",
            params: [tokenId, address],
        }),
    };
};
class ParallelBalanceAdapter extends balance_adapter_1.BalanceAdapter {
    constructor({ api, chain, tokens }) {
        super({ api, chain, tokens });
        this.storages = createBalanceStorages(api);
    }
    subscribeBalance(token, address) {
        const storage = this.storages.balances(address);
        if (token === this.nativeToken) {
            return storage.observable.pipe((0, rxjs_1.map)((data) => ({
                free: sdk_core_1.FixedPointNumber.fromInner(data.freeBalance.toString(), this.decimals),
                locked: sdk_core_1.FixedPointNumber.fromInner(data.lockedBalance.toString(), this.decimals),
                reserved: sdk_core_1.FixedPointNumber.fromInner(data.reservedBalance.toString(), this.decimals),
                available: sdk_core_1.FixedPointNumber.fromInner(data.availableBalance.toString(), this.decimals),
            })));
        }
        const tokenId = SUPPORTED_TOKENS[token];
        if (tokenId === undefined) {
            throw new errors_1.CurrencyNotFound(token);
        }
        return this.storages.assets(tokenId, address).observable.pipe((0, rxjs_1.map)((balance) => {
            var _a, _b;
            const amount = sdk_core_1.FixedPointNumber.fromInner(((_b = (_a = balance.unwrapOrDefault()) === null || _a === void 0 ? void 0 : _a.balance) === null || _b === void 0 ? void 0 : _b.toString()) || "0", this.getToken(token).decimals);
            return {
                free: amount,
                locked: new sdk_core_1.FixedPointNumber(0),
                reserved: new sdk_core_1.FixedPointNumber(0),
                available: amount,
            };
        }));
    }
}
class BaseParallelAdapter extends base_chain_adapter_1.BaseCrossChainAdapter {
    async setApi(api) {
        this.api = api;
        await api.isReady;
        const chain = this.chain.id;
        this.balanceAdapter = new ParallelBalanceAdapter({
            chain,
            api,
            tokens: exports.parallelTokensConfig[chain],
        });
    }
    subscribeTokenBalance(token, address) {
        if (!this.balanceAdapter) {
            throw new errors_1.ApiNotFound(this.chain.id);
        }
        return this.balanceAdapter.subscribeBalance(token, address);
    }
    subscribeMaxInput(token, address, to) {
        var _a;
        if (!this.balanceAdapter) {
            throw new errors_1.ApiNotFound(this.chain.id);
        }
        return (0, rxjs_1.combineLatest)({
            txFee: token === ((_a = this.balanceAdapter) === null || _a === void 0 ? void 0 : _a.nativeToken)
                ? this.estimateTxFee({
                    amount: sdk_core_1.FixedPointNumber.ZERO,
                    to,
                    token,
                    address,
                    signer: address,
                })
                : "0",
            balance: this.balanceAdapter
                .subscribeBalance(token, address)
                .pipe((0, rxjs_1.map)((i) => i.available)),
        }).pipe((0, rxjs_1.map)(({ balance, txFee }) => {
            var _a;
            const tokenMeta = (_a = this.balanceAdapter) === null || _a === void 0 ? void 0 : _a.getToken(token);
            const feeFactor = 1.2;
            const fee = sdk_core_1.FixedPointNumber.fromInner(txFee, tokenMeta === null || tokenMeta === void 0 ? void 0 : tokenMeta.decimals).mul(new sdk_core_1.FixedPointNumber(feeFactor));
            // always minus ed
            return balance
                .minus(fee)
                .minus(sdk_core_1.FixedPointNumber.fromInner((tokenMeta === null || tokenMeta === void 0 ? void 0 : tokenMeta.ed) || "0", tokenMeta === null || tokenMeta === void 0 ? void 0 : tokenMeta.decimals));
        }));
    }
    createTx(params) {
        if (this.api === undefined) {
            throw new errors_1.ApiNotFound(this.chain.id);
        }
        const { address, amount, to, token } = params;
        const toChain = configs_1.chains[to];
        const accountId = this.api.createType("AccountId32", address).toHex();
        const tokenId = SUPPORTED_TOKENS[token];
        if (tokenId === undefined) {
            throw new errors_1.CurrencyNotFound(token);
        }
        return xtokens_helper_1.xTokensHelper.transfer(this.api, this.chain, toChain, accountId, token, tokenId, amount, this.getDestWeight(token, to) || "Unlimited");
    }
}
class HeikoAdapter extends BaseParallelAdapter {
    constructor() {
        super(configs_1.chains.heiko, exports.heikoRoutersConfig, exports.parallelTokensConfig.heiko);
    }
}
exports.HeikoAdapter = HeikoAdapter;
class ParallelAdapter extends BaseParallelAdapter {
    constructor() {
        super(configs_1.chains.parallel, exports.parallelRoutersConfig, exports.parallelTokensConfig.parallel);
    }
}
exports.ParallelAdapter = ParallelAdapter;
